import type {getOrganizationParams, getOrganizationsParams} from '$server/domain/mydata/models'
import type {Industry, OrgType} from '$share/types/common'
import type {z} from 'zod'

export type GetOrganizationsParams = z.infer<typeof getOrganizationsParams>

enum CapitalOrgType {
    Capital = 'capital',
    Financial = 'financial',
    CardBank = 'cardbank',
}

enum InsuranceOrgType {
    Life = 'life',
    Property = 'property',
    Etc = 'etc',
}

type ExtendedInfo = Readonly<{
    homeUrl: string // 공식홈페이지 URL
    demandUrl: string // 청구 URL
    serviceCenterUrl: string // 고객센터 URL
    serviceCenterTel: string // 대표콜센터 전화번호
    emergencyTel: string // 긴급출동/사고접수 전화번호
    mileageRefundUrl: string // 마일리지 환급 URL
    mileageRefundRates: {
        // 마일리지 환급 비율 목록
        km: number // 기준거리
        rate: number // 환급비율
    }[]
    updateAt: string // 수정시각
    finalRegUser: string // 수정자
}>

export interface Organization {
    orgCode: string // 기관코드
    name: string // 기관이름 ex) "주식회사 신한은행"
    displayName: string // 내자산 표시이름 ex) "신한"
    displayName2: string // 내자산 표시이름 (두번째) ex) "신한은행"
    industry: Industry // 업권정보
    type: OrgType // 기관타입
    orgRegno: string // 기관 사업자등록번호
    authType: string // 제공인증방식
    payOrgCode: string // 3자리기관코드
    relayOrgCode: string // 중계기관코드
    openbankingOrgCode: string // 오픈뱅킹 기관코드
    payCardId: string // 페이관리 카드사 코드
    cardIssuerCode: string | null // 카드 발급사 코드
    payAccountSupport: boolean // 페이 간편결제 지원여부
    cardPointChangeSupport: boolean // 카드사포인트 전환 가능 여부
    odr: number // 업권별 순서
    enabled: boolean // 내자산 서비스 서비스여부
    display: boolean // 내자산 서비스 노출여부
    savings: boolean // 저축은행 여부
    orgSubType: InsuranceOrgType | CapitalOrgType | null // 기관 하위 유형
    extendedInfo: ExtendedInfo | null // 확장정보
}

export type OrganizationParams = z.infer<typeof getOrganizationParams>

export type SimpleOrganization = Pick<
    Organization,
    | 'orgCode'
    | 'displayName'
    | 'displayName2'
    | 'industry'
    | 'payOrgCode'
    | 'payCardId'
    | 'cardIssuerCode'
    | 'savings'
    | 'cardPointChangeSupport'
>
export type Org = Pick<Organization, 'orgCode' | 'name' | 'displayName' | 'displayName2' | 'industry'>
export type AssetOrg = Pick<
    Organization,
    | 'orgCode'
    | 'name'
    | 'displayName'
    | 'displayName2'
    | 'industry'
    | 'payOrgCode'
    | 'enabled'
    | 'payAccountSupport'
    | 'odr'
>
